import { useState } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import InputDate from '@/design_system/InputDate';
import { dateStringToCalendarDate } from '@/design_system/InputDate/InputDate';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useSetManualStatusDueAt } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';

const ManuallyUpdateStatusDueAtDialog = ({
  article,
  isOpen,
  setIsOpen,
}: {
  article: ArticleWithRelations;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const isInRepair = article.taskType === 'repair_article';

  return (
    <Dialog
      title={
        isInRepair ? (
          <Trans id="manually-update-repair-completion-date.title">
            Update repair completion date
          </Trans>
        ) : (
          <Trans id="manually-update-exchange-completion-date.title">
            Update exchange completion date
          </Trans>
        )
      }
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      style={{ maxWidth: '500px' }}
    >
      <ManuallyUpdateStatusDueAtForm article={article} close={() => setIsOpen(false)} />
    </Dialog>
  );
};

const ManuallyUpdateStatusDueAtForm = ({
  article,
  close,
}: {
  article: ArticleWithRelations;
  close: () => void;
}) => {
  const { t } = useLingui();
  const isInRepair = article.taskType === 'repair_article';

  const [statusDueAt, setStatusDueAt] = useState(
    article.statusDueAt ? dateStringToCalendarDate(article.statusDueAt)?.toString() : null
  );
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const { mutateAsync: setManualStatusDueAt, isPending } = useSetManualStatusDueAt({
    articleId: article.id,
  });

  const handleStatusDueAtUpdate = async () => {
    setShowErrors(true);

    if (!statusDueAt) {
      return;
    }

    const body: Endpoints['PATCH /articles/:id/status-due-at']['body'] = {
      statusDueAt: statusDueAt,
    };

    try {
      await setManualStatusDueAt(body);
      close();
    } catch (err: any) {
      console.error(err);
      setError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="8px">
          <Paragraph>
            {isInRepair ? (
              <Trans id="manually-update-repair-completion-date.text">
                You're about to update the estimated repair completion date. The new date will be
                sent to the request supervisor.
              </Trans>
            ) : (
              <Trans id="manually-update-exchange-completion-date.text">
                You're about to update the estimated exchange completion date. The new date will be
                sent to the request supervisor.
              </Trans>
            )}
          </Paragraph>
          <InputDate
            label={
              isInRepair ? (
                <Trans id="manually-update-repair-completion-date.title.label">
                  Repair completion date
                </Trans>
              ) : (
                <Trans id="manually-update-exchange-completion-date.title.label">
                  Exchange completion date
                </Trans>
              )
            }
            value={statusDueAt}
            onChange={(date) => setStatusDueAt(date)}
            style={{ flex: 1 }}
            minValue={today(getLocalTimeZone())}
            error={
              showErrors && !statusDueAt
                ? isInRepair
                  ? t({
                      id: 'manually-update-repair-completion-date.error.invalid',
                      message: 'Please select a repair completion date',
                    })
                  : t({
                      id: 'manually-update-exchange-completion-date.error.invalid',
                      message: 'Please select an exchange completion date',
                    })
                : undefined
            }
          />
        </Stack>
      </main>
      <Footer>
        {error !== null && <FooterError>{error}</FooterError>}
        <FooterButtons>
          <Button variant="primary" onPress={handleStatusDueAtUpdate} isLoading={isPending}>
            <Trans id="manually-update-repair-completion-date.title.report">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

export default ManuallyUpdateStatusDueAtDialog;
